import { BookingSettings } from '@/@type/booking';
import { IGoogleIntegrationInfo } from '@/@type/google/feeds/settings';
import { Merchant } from '@/__generated__/types';
import { bookingMerchantBookingSettingsQuery, bookingPublicMerchantQuery, queryGooglePublicSettings } from '@/_apis_/booking/axios';
import { useSnackbarHelper } from '@/components/useSnackbarHelper';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

const DEFAULT_MERCHANT_ID = 'orderbuddy';

interface IUseFetchMerchantReturn {
  merchant: Merchant | null;
  bookingSettings: BookingSettings | null;
  googleSettings: IGoogleIntegrationInfo | null;
  fetchMerchantLoading: boolean;
  fetchMerchantError: string | null;
}

/**
 * Custom Hook for fetching merchant data.
 * @param merchantId - The ID of the merchant to fetch.
 * @returns Merchant data and loading/error states.
 */
export const useFetchMerchant = (merchantId: string): IUseFetchMerchantReturn => {
  const { t } = useTranslation();
  const { showSnackbar } = useSnackbarHelper();
  const [merchant, setMerchant] = useState<Merchant | null>(null);
  const [bookingSettings, setBookingSettings] = useState<BookingSettings | null>(null);
  const [googleSettings, setGoogleSettings] = useState<IGoogleIntegrationInfo | null>(null);
  const [fetchMerchantLoading, setFetchMerchantLoading] = useState<boolean>(true);
  const [fetchMerchantError, setFetchMerchantError] = useState<string | null>(null);
  useEffect(() => {
    const fetchMerchant = async (): Promise<void> => {
      if (merchantId === DEFAULT_MERCHANT_ID) {
        setFetchMerchantLoading(false);
        return;
      }
      setFetchMerchantLoading(true);
      setFetchMerchantError(null);
      try {
        // 并行执行所有查询
        const [googleSettingsResp, merchantResp, bookingSettingsResp] = await Promise.all([
          queryGooglePublicSettings(merchantId),
          bookingPublicMerchantQuery(merchantId),
          bookingMerchantBookingSettingsQuery(merchantId)
        ]);

        // 处理 Google 设置
        if (googleSettingsResp && googleSettingsResp.data) {
          setGoogleSettings(googleSettingsResp.data);
        } else {
          setGoogleSettings(null);
        }

        // 处理商户数据
        if (!merchantResp) {
          const errorMessage = t('useFetchMerchant.merchantNotFound');
          showSnackbar(errorMessage, 'error');
          setFetchMerchantError(errorMessage);
          console.error(errorMessage);
          return;
        }
        setMerchant(merchantResp);

        // 处理预订设置
        if (!bookingSettingsResp) {
          const errorMessage = t('useFetchMerchant.bookingSettingsNotFound');
          showSnackbar(errorMessage, 'error');
          setFetchMerchantError(errorMessage);
          console.error(errorMessage);
          return;
        }
        setBookingSettings(bookingSettingsResp);
      } catch (err) {
        const errorMessage = t('useFetchMerchant.failedToFetchMerchantData');
        showSnackbar(errorMessage, 'error');
        setFetchMerchantError(errorMessage);
        console.error(err);
      } finally {
        setFetchMerchantLoading(false);
      }
    };

    fetchMerchant();
  }, [merchantId, showSnackbar, t]);

  return { merchant, bookingSettings, googleSettings, fetchMerchantLoading, fetchMerchantError };
};
